import { StylePropertyDefinition } from '../../types/common';

/**
 * The page editor's palette, type scale, shape scale and elevation ramp, as
 * theme variables, plus a full dark set.
 *
 * `dist/css/PageEditor.css` hard-codes its own greys, a `#52bd94` green, a
 * `#1893e9` blue, `Inter` in 42 places, 16 different border-radii and 16
 * different box-shadows. That is fine for a stock editor and wrong inside an app
 * with a design language of its own. `PageEditorStyle.tsx` appends
 * `processStyleDefinition(...)` output AFTER that stylesheet, so declaring a
 * property here overrides the literal by cascade alone, without touching a line
 * of the layout CSS.
 *
 * Some selectors here end in `:not(:where(._peSidekickBar *))`. Those are the
 * ones that reach a bare element straight from the editor root (`i.fa`,
 * `button`, `input`, `textarea`, `select`), which outranks any embedded
 * component's own class-scoped rules and repaints it. The docked AI panel
 * renders the ordinary Prompt component inside the editor, and without the
 * exclusion its icons came out black on a black button and its textarea picked
 * up editor chrome. `:where()` contributes no specificity, so this narrows what
 * the rule matches without moving it in the cascade. Keep it on any new rule
 * that names a bare element with no class between it and the root.
 *
 * Every `dv` is the literal the component ships today, except where several
 * near-identical literals were folded onto one scale step and the most common
 * value kept as the default. An app that sets none of these is unchanged but for
 * those folds. A property with no `dv` (label tracking) emits nothing at all
 * until a theme supplies one.
 *
 * DARK SET: `LazyPageEditor` renders `comp compPageEditor ${localTheme}` on the
 * root, so every `pageEditorDark*` entry is scoped to
 * `.comp.compPageEditor._dark` -- one class more than its light twin, so it wins
 * on specificity and the order of the two blocks in this array does not matter.
 * The contrast button in `DnDTopBar` flips `personalization.theme` between
 * `_light` and `_dark`.
 *
 * Selectors are written out in full with `np: true`, because the automatic
 * prefix only lands on the first branch of a comma-separated list, and they are
 * ordered to match the base stylesheet's own source order so ties between
 * equally specific rules resolve the way they always did.
 *
 * Only the light editor chrome (the first ~3750 lines of the stylesheet) is
 * covered. The AI spotlight, the KIRun dialog and the debug viewer are separate
 * dark surfaces driven by their own `--pageEditor*` custom properties.
 *
 * Generated from the stylesheet. If you edit PageEditor.css, re-derive this
 * rather than hand-patching it.
 */
export const styleProperties: Array<StylePropertyDefinition> = [
	{
		gn: 'Page Editor Type',
		dn: 'Font Family',
		de: 'Every label, input and button in the editor. Point this at the app own face and the editor stops reading as a separate tool.',
		n: 'pageEditorFontFamily',
		dv: 'Inter',
		cp: 'font-family',
		sel: '.comp.compPageEditor ._compsTree ._treeNode, .comp.compPageEditor select._peSelect, .comp.compPageEditor ._peSelect, .comp.compPageEditor ._simpleEditorSelect._peSelect, .comp.compPageEditor ._filterBar input, .comp.compPageEditor input._peInput, .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor textarea._peInput, .comp.compPageEditor select._peInput, .comp.compPageEditor ._textValueEditorContainer, .comp.compPageEditor ._iconMenuOption, .comp.compPageEditor ._popupMenuBackground ._popupMenuItem, .comp.compPageEditor ._iconMenuBody, .comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer input, .comp.compPageEditor ._simpleEditorInputWithIcon, .comp.compPageEditor ._simpleEditorSelect, .comp.compPageEditor ._simpleEditorInput, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption, .comp.compPageEditor ._simpleEditor._warning, .comp.compPageEditor ._simpleEditorBigSelector input, .comp.compPageEditor ._simpleEditorGroupTitle, .comp.compPageEditor ._dropdownToggle, .comp.compPageEditor ._dropdownToggle._open, .comp.compPageEditor ._simpleEditorGroupContent ._editorLine ._label, .comp.compPageEditor ._simpleEditorButtonBar, .comp.compPageEditor ._detailStyleEditor ._header, .comp.compPageEditor ._simpleLabel, .comp.compPageEditor ._spacingEditor ._label, .comp.compPageEditor ._spacingEditor ._changer ._header, .comp.compPageEditor ._spacingEditor ._value, .comp.compPageEditor ._iframeHeader, .comp.compPageEditor ._propLabel, .comp.compPageEditor ._propertyGroupHeader',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Mono Font Family',
		de: 'Code and expression areas.',
		n: 'pageEditorFontFamilyMono',
		dv: 'monospace',
		cp: 'font-family',
		sel: '.comp.compPageEditor ._propertyGroupHeaderIcon',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Family',
		de: 'Every label, input and button in the editor. Point this at the app own face and the editor stops reading as a separate tool.',
		n: 'pageEditorFontFamily',
		dv: 'Inter',
		cp: 'font-family',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formButton ._backButton, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorHeader, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorSubHeader, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorFilesTitle, .comp.compPageEditor ._formEditorOptionsPagination ._paginationPrev, .comp.compPageEditor ._formEditorOptionsPagination ._paginationPages, .comp.compPageEditor ._formEditorOptionsPagination ._paginationNext, .comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionName, .comp.compPageEditor ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldsAndButtonsTitle, .comp.compPageEditor ._popupContainer._formEditor ._formFieldAndButton ._formFieldAndButtonTitle, .comp.compPageEditor ._tabContainer, .comp.compPageEditor ._tabContainer ._tab, .comp.compPageEditor ._topRightBarGrid button, .comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XL',
		de: 'Popup and panel titles.',
		n: 'pageEditorFontSizeXLarge',
		dv: '22px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._filterBar i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._compsTree ._treeNode',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._compsTree ._treeNode i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._compsTree ._treeNodeName i.fa.fa-caret-right',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XL',
		de: 'Popup and panel titles.',
		n: 'pageEditorFontSizeXLarge',
		dv: '22px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._sideBar ._top i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor select._peSelect, .comp.compPageEditor ._peSelect, .comp.compPageEditor ._simpleEditorSelect._peSelect',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor button:not(:where(._peSidekickBar *)), .comp.compPageEditor ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._filterBar input, .comp.compPageEditor input._peInput, .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor textarea._peInput, .comp.compPageEditor select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._eachStyleClass ._propLabel i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: ".comp.compPageEditor input._peInput[type='text'], .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor input._peInput[type='number']",
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XL',
		de: 'Popup and panel titles.',
		n: 'pageEditorFontSizeXLarge',
		dv: '22px',
		cp: 'font-size',
		sel: '.comp.compPageEditor i.fa:not(:where(._peSidekickBar *))',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._iconMenuBody',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl ._control',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer input, .comp.compPageEditor ._simpleEditorInputWithIcon, .comp.compPageEditor ._simpleEditorSelect, .comp.compPageEditor ._simpleEditorInput, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._simpleEditor._warning',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector input',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchResult ._searchResultItem ._animationName',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle, .comp.compPageEditor ._dropdownToggle, .comp.compPageEditor ._dropdownToggle._open, .comp.compPageEditor ._simpleEditorGroupContent ._editorLine ._label, .comp.compPageEditor ._simpleEditorButtonBar, .comp.compPageEditor ._detailStyleEditor ._header, .comp.compPageEditor ._simpleLabel',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._spacingEditor ._label',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._spacingEditor ._changer ._header',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._spacingEditor ._value',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._spacingEditor ._padding ._value',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._buttonBarText, .comp.compPageEditor ._iframeHeader',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._eachSelectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._eachSelectionBar i.fa, .comp.compPageEditor ._iconMenuBody i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._eachProp',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle::before, .comp.compPageEditor ._description',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._tooltip:hover::after',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XS',
		de: 'Badges and unit suffixes.',
		n: 'pageEditorFontSizeXSmall',
		dv: '10px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._pillTag',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XL',
		de: 'Popup and panel titles.',
		n: 'pageEditorFontSizeXLarge',
		dv: '22px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._propertyGroupHeader',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._propertyGroupHeaderIcon',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._eachProperty i.fa._controlIcons',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formButton ._backButton, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorHeader',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorSubHeader, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorFilesTitle',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._formEditorOptionsPagination ._paginationPrev, .comp.compPageEditor ._formEditorOptionsPagination ._paginationPages, .comp.compPageEditor ._formEditorOptionsPagination ._paginationNext',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionName, .comp.compPageEditor ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldsAndButtonsTitle',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formFieldAndButton ._formFieldAndButtonTitle',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon, .comp.compPageEditor ._popupBackground ._popupContainer ._eachIcon input',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupMenuBackground ._popupMenuContainer',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._tabContainer',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._tabContainer ._tab, .comp.compPageEditor ._topRightBarGrid button, .comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size L',
		de: 'Section headers and buttons.',
		n: 'pageEditorFontSizeLarge',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._tutorialHeader h3',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size XL',
		de: 'Popup and panel titles.',
		n: 'pageEditorFontSizeXLarge',
		dv: '22px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._youtubeButton i',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size Base',
		de: 'The workhorse size: inputs, tree rows, menu items.',
		n: 'pageEditorFontSizeBase',
		dv: '12px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._tutorialDescription',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Font Size S',
		de: 'Property labels and tab captions.',
		n: 'pageEditorFontSizeSmall',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._compsTree ._treeNode',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor select._peSelect, .comp.compPageEditor ._peSelect, .comp.compPageEditor ._simpleEditorSelect._peSelect',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor button:not(:where(._peSidekickBar *)), .comp.compPageEditor ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._filterBar input, .comp.compPageEditor input._peInput, .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor textarea._peInput, .comp.compPageEditor select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._filterBar input',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: ".comp.compPageEditor ._addSelector, .comp.compPageEditor input._peInput[type='text'], .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor input._peInput[type='number'], .comp.compPageEditor ._pvExpressionEditor",
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._pvExpressionEditor ._pathsList, .comp.compPageEditor ._pvExpressionEditor ._pathsList ._path',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._urlInput._peInput, .comp.compPageEditor ._textValueEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._iconMenuBody',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._iconMenu',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius L',
		de: 'Popups, panels and pill controls.',
		n: 'pageEditorRadiusLarge',
		dv: '10px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._buttonBar i.fa, .comp.compPageEditor ._buttonBar svg',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._propLabel ._svgButtonsContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl, .comp.compPageEditor ._scaleControlContainer ._scaleControl ._control._text',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius L',
		de: 'Popups, panels and pill controls.',
		n: 'pageEditorRadiusLarge',
		dv: '10px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._microToggle2, .comp.compPageEditor ._microToggle2._withText',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor ._simpleEditorInputWithIcon, .comp.compPageEditor ._simpleEditorSelect, .comp.compPageEditor ._simpleEditorInput',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius L',
		de: 'Popups, panels and pill controls.',
		n: 'pageEditorRadiusLarge',
		dv: '10px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._page_Selector ._simpleEditorSelect',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._add_page_btn_container button._add_page_btn',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._simpleEditorIcons, .comp.compPageEditor ._simpleEditorIcons ._eachIcon, .comp.compPageEditor ._simpleEditorBigSelector ._searchBox',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchResult ._searchResultItem ._animationIcon',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._dropdownToggle',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._dropdownContent, .comp.compPageEditor ._simpleEditorButtonBar, .comp.compPageEditor ._simpleEditorButtonBar ._simpleButtonBarButton',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._detailStyleEditor, .comp.compPageEditor ._spacingEditor._margin, .comp.compPageEditor ._spacingEditor ._padding',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._spacingEditor ._changer, .comp.compPageEditor ._spacingEditor ._value, .comp.compPageEditor ._spacingEditor ._square',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._iframeHeader, .comp.compPageEditor ._styleButtonContainer button, .comp.compPageEditor ._styleButtonContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._addArrayItemButton, .comp.compPageEditor ._eachProp, .comp.compPageEditor ._pvEditor ._microToggle, .comp.compPageEditor ._pvEditor ._microToggle::before, .comp.compPageEditor ._propValue._padding, .comp.compPageEditor ._tooltip:hover::after',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius L',
		de: 'Popups, panels and pill controls.',
		n: 'pageEditorRadiusLarge',
		dv: '10px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._pillTag',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._iconSelectionButton, .comp.compPageEditor ._propertyGroupHeader, .comp.compPageEditor ._eachProperty i.fa._controlIcons, .comp.compPageEditor ._codeEditorContent, .comp.compPageEditor ._codeEditorContent ._iconMenuSeperator, .comp.compPageEditor ._addPropertyButtonContainer button, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton, .comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionPreview, .comp.compPageEditor ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldAndButton, .comp.compPageEditor ._popupBackground ._popupContainer ._jsonEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._pathParts span._clickable, .comp.compPageEditor ._popupBackground ._popupContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._tabContainer',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._topRightBarGrid button, .comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch, .comp.compPageEditor ._popupMenuContainer._compMenu ._toggleButton',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius L',
		de: 'Popups, panels and pill controls.',
		n: 'pageEditorRadiusLarge',
		dv: '10px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._tutorialTooltipPanel, .comp.compPageEditor ._youtubeButton, .comp.compPageEditor ._videoContainer, .comp.compPageEditor ._videoContainer iframe, .comp.compPageEditor ._videoContainer video',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius S',
		de: 'Inputs, chips, small buttons.',
		n: 'pageEditorRadiusSmall',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._sectionThumb',
		np: true,
	},
	{
		gn: 'Page Editor Shape',
		dn: 'Radius M',
		de: 'Cards, menu items, toolbars.',
		n: 'pageEditorRadiusMedium',
		dv: '6px',
		cp: 'border-radius',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorShadowMedium',
		dv: '0px 2px 4px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._propBar._right',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorShadowLarge',
		dv: '0 15px 30px 0 rgba(0, 0, 0, 0.1), 0 5px 15px 0 rgba(0, 0, 0, 0.1)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._pvExpressionEditor ._pathsList',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorShadowSmall',
		dv: '0px 1px 3px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._urlInput._peInput, .comp.compPageEditor ._iconMenuBody, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorShadowMedium',
		dv: '0px 2px 4px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon:hover, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon._active',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorShadowSmall',
		dv: '0px 1px 3px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._dropdownContent, .comp.compPageEditor ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorShadowLarge',
		dv: '0 15px 30px 0 rgba(0, 0, 0, 0.1), 0 5px 15px 0 rgba(0, 0, 0, 0.1)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._detailStyleEditor',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorShadowMedium',
		dv: '0px 2px 4px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._spacingEditor ._changer',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorShadowSmall',
		dv: '0px 1px 3px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._styleButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorShadowMedium',
		dv: '0px 2px 4px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorShadowLarge',
		dv: '0 15px 30px 0 rgba(0, 0, 0, 0.1), 0 5px 15px 0 rgba(0, 0, 0, 0.1)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._codeEditorContent, .comp.compPageEditor ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorShadowSmall',
		dv: '0px 1px 3px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorShadowMedium',
		dv: '0px 2px 4px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption:hover ._formEditorEachOptionPreview, .comp.compPageEditor ._popupMenuContainer._compMenu._show._compMenuRight, .comp.compPageEditor ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorShadowSmall',
		dv: '0px 1px 3px 0px #0000001a',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._tutorialTooltipPanel',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorShadowLarge',
		dv: '0 15px 30px 0 rgba(0, 0, 0, 0.1), 0 5px 15px 0 rgba(0, 0, 0, 0.1)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'background',
		sel: '.comp.compPageEditor ._addSelector',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background',
		sel: '.comp.compPageEditor ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._sideBar ._iconMenu',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorInverseBackground',
		dv: '#000000',
		cp: 'background',
		sel: '.comp.compPageEditor ._sideBar ._iconMenu._active, .comp.compPageEditor ._sideBar ._iconMenu:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._buttonBar i.fa, .comp.compPageEditor ._buttonBar svg',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background',
		sel: '.comp.compPageEditor ._iframeHeader._screenSizes svg',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._propLabel._svgButtons .svgContainer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorSelectedBackground',
		dv: 'rgba(77, 127, 238, 0.05)',
		cp: 'background',
		sel: '.comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'background',
		sel: '.comp.compPageEditor ._add_page_btn_container button._add_page_btn',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchResult ._searchResultItem ._animationIcon',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle._gradient',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._simpleEditorGroup ._simpleEditorGroupContent',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Tutorial Banner Gradient',
		de: 'The two-tone banner at the head of the tutorial panel.',
		n: 'pageEditorHeaderGradient',
		dv: 'linear-gradient(150deg, #43B2FF 13.39%, #52BD94 86.61%)',
		cp: 'background',
		sel: '.comp.compPageEditor ._simpleEditorAngle ._angleTrack::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._spacingEditor ._value, .comp.compPageEditor ._tabBar, .comp.compPageEditor ._styleButtonContainer',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background',
		sel: '.comp.compPageEditor ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorInverseBackground',
		dv: '#000000',
		cp: 'background',
		sel: '.comp.compPageEditor .commonTriStateCheckbox::before',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorBottomBorder, .comp.compPageEditor ._paginationContainer ._pageButton',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'background',
		sel: '.comp.compPageEditor ._paginationContainer ._pageButtonActive',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionPreview, .comp.compPageEditor ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldAndButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Overlay Scrim',
		de: 'The dimming layer behind a modal popup.',
		n: 'pageEditorScrimBackground',
		dv: '#0004',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupBackground',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Save Button Gradient',
		de: 'Fill of the Save button, the editor primary action.',
		n: 'pageEditorSaveGradient',
		dv: 'linear-gradient(180deg, #4BC6FF 0%, #1893E9 100%)',
		cp: 'background',
		sel: '.comp.compPageEditor ._topRightBarGrid button._save',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Publish Button Gradient',
		de: 'Fill of the Publish button. Its only job is to stay distinguishable from Save.',
		n: 'pageEditorPublishGradient',
		dv: 'linear-gradient(180deg, #7CD9B6 0%, #52BD94 100%)',
		cp: 'background',
		sel: '.comp.compPageEditor ._topRightBarGrid button._publish',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorSurfaceStrong',
		dv: '#DDDDDD',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._toggleButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._toggleButton::after',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background',
		sel: '.comp.compPageEditor ._tutorialTooltipPanel',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorInverseBackground',
		dv: '#000000',
		cp: 'background',
		sel: '.comp.compPageEditor ._youtubeButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._dndGrid',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._topBarGrid, .comp.compPageEditor ._sideBar, .comp.compPageEditor ._propBar, .comp.compPageEditor ._peSidekickBar',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorSelectedBackground',
		dv: 'rgba(77, 127, 238, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._compsTree ._treeNode._selected, .comp.compPageEditor ._compsTree ._treeNode:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor select._peSelect, .comp.compPageEditor ._peSelect, .comp.compPageEditor ._simpleEditorSelect._peSelect',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor button:not(:where(._peSidekickBar *)), .comp.compPageEditor ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._filterBar input, .comp.compPageEditor input._peInput, .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor textarea._peInput, .comp.compPageEditor select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'background-color',
		sel: '.comp.compPageEditor textarea._peInput::-webkit-scrollbar-thumb',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvExpressionEditor ._pathsList',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvExpressionEditor ._pathsList ._path:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._textValueEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._sideBar ._seperator',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorSelectedBackground',
		dv: 'rgba(77, 127, 238, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor button:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor select:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor ._iconMenuOption:hover, .comp.compPageEditor ._popupButtons button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iconMenuBody',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorSelectedBackground',
		dv: 'rgba(77, 127, 238, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iconMenu._active',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iconMenu:hover, .comp.compPageEditor ._iconMenuBody ._iconMenuOption:hover, .comp.compPageEditor ._popupMenuBackground ._popupMenuItem:hover, .comp.compPageEditor ._tabContainer ._tab:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._buttonBar._lightBackground',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorSelectedBackground',
		dv: 'rgba(77, 127, 238, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._buttonBar i.fa.active, .comp.compPageEditor ._buttonBar i.fa:hover, .comp.compPageEditor ._buttonBar svg.active, .comp.compPageEditor ._buttonBar svg:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl ._control._text, .comp.compPageEditor ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorSurfaceStrong',
		dv: '#DDDDDD',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2::before',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2._on, .comp.compPageEditor ._microToggle2:hover::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor ._simpleEditorInputWithIcon, .comp.compPageEditor ._simpleEditorSelect, .comp.compPageEditor ._simpleEditorInput',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered, .comp.compPageEditor ._simpleEditorIcons._bground',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon:hover, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon._active',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchBox',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._dropdownToggle._open, .comp.compPageEditor ._dropdownContent',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._dropdownContent ._eachIcon:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorAngle, .comp.compPageEditor ._simpleEditorButtonBar',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._detailStyleEditor, .comp.compPageEditor ._spacingEditor ._changer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._spacingEditor ._changer ._header',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._spacingEditor ._square, .comp.compPageEditor ._spacingEditor ._circle',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._spacingEditor ._square._hasValue, .comp.compPageEditor ._spacingEditor ._circle._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._screenSizes:hover, .comp.compPageEditor ._iframeHeader, .comp.compPageEditor ._selectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorSurfaceStrong',
		dv: '#DDDDDD',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._previewModeCloser',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._styleButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle::before, .comp.compPageEditor ._pvEditor ._microToggle._on',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorSurfaceStrong',
		dv: '#DDDDDD',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._propValue._padding',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorSurfaceHover',
		dv: 'rgba(0, 0, 0, 0.05)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._description',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._tooltip:hover::after, .comp.compPageEditor ._pillTag, .comp.compPageEditor ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iconSelectionButton.active, .comp.compPageEditor ._eachProperty i.fa._controlIcons',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._codeEditorContent',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._codeEditorContent ._codeEditorHeader',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Soft Background',
		de: 'A translucent accent fill used behind hovered accent controls.',
		n: 'pageEditorAccentSoftBackground',
		dv: '#52BD94CC',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._addPropertyButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorSurfaceStrong',
		dv: '#DDDDDD',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._eachIcon input, .comp.compPageEditor ._popupBackground ._popupContainer ._pathParts span._clickable:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer, .comp.compPageEditor ._popupMenuBackground ._popupMenuContainer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._tabContainer',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorSurfaceAlt',
		dv: '#EEEEEE',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._sectionThumb:hover',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorBackground',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor button:not(:where(._peSidekickBar *)), .comp.compPageEditor ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._filterBar input, .comp.compPageEditor ._scaleControlContainer ._scaleControl',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Heavy Border Color',
		de: 'The darkest rule in the editor, the pill toggle track.',
		n: 'pageEditorBorderColorHeavy',
		dv: '#888888',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Border Color',
		de: 'The darker rim drawn around a filled accent control.',
		n: 'pageEditorAccentBorderColor',
		dv: '#198A61',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._microToggle2:hover',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Heavy Border Color',
		de: 'The darkest rule in the editor, the pill toggle track.',
		n: 'pageEditorBorderColorHeavy',
		dv: '#888888',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._microToggle2::before',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Border Color',
		de: 'The darker rim drawn around a filled accent control.',
		n: 'pageEditorAccentBorderColor',
		dv: '#198A61',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._microToggle2._on, .comp.compPageEditor ._microToggle2:hover::before',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Inverse Border Color',
		de: 'A border drawn on an inverted ground.',
		n: 'pageEditorBorderColorInverse',
		dv: '#FFFFFF',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._microToggle2._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchBox',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._dropdownContent',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Highlight Border Color',
		de: 'The bright ring around the angle dial in the style editor.',
		n: 'pageEditorHighlightBorderColor',
		dv: '#43B2FF',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._simpleEditorGroupContent ._simpleEditorAngle',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._detailStyleEditor',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Button Border Color',
		de: 'The ring around a selectable icon button.',
		n: 'pageEditorIconBorderColor',
		dv: '#E3E5EA',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._spacingEditor._margin',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._spacingEditor._margin._hasValue, .comp.compPageEditor ._spacingEditor ._padding._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Button Border Color',
		de: 'The ring around a selectable icon button.',
		n: 'pageEditorIconBorderColor',
		dv: '#E3E5EA',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._spacingEditor ._padding',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._iframeHeader',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._propValue._padding',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._tooltip:hover::after, .comp.compPageEditor ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._codeEditorContent ._iconMenuSeperator',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Border Color',
		de: 'Outline around a focused or highlighted informational control.',
		n: 'pageEditorInfoBorderColor',
		dv: 'rgba(66, 126, 228, 0.8)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption:hover ._formEditorEachOptionPreview',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._jsonEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._eachIcon._upload',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._popupMenuBackground ._popupMenuContainer',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Focus Border Color',
		de: 'The rim on a focused search box.',
		n: 'pageEditorFocusBorderColor',
		dv: '#000000',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-color',
		sel: '.comp.compPageEditor ._tutorialTooltipPanel, .comp.compPageEditor ._popupMenuContainer._compMenu ._sectionThumb, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem.active',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor ._sideBar, .comp.compPageEditor ._propBar._left',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Dotted Guide Color',
		de: 'The dotted guide between spacing-editor cells.',
		n: 'pageEditorBorderColorDotted',
		dv: '#00000020',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor ._compsTree:hover ._treeNodeLevel, .comp.compPageEditor ._compsTree:hover ._treeNode._subComponent:hover ._treeNodeLevel, .comp.compPageEditor ._compsTree:hover ._treeNode._subComponent._selected ._treeNodeLevel, .comp.compPageEditor ._compsTree ._treeNodeLevel._lastOpened',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor ._styleButtonContainer ._seperator',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._left, .comp.compPageEditor ._popupMenuContainer._compMenu._show._compMenuRight',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor ._topBarGrid, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._options_divider, .comp.compPageEditor ._detailStyleEditor ._header, .comp.compPageEditor ._spacingEditor ._changer ._header',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Tab Indicator Color',
		de: 'The underline beneath the selected tab.',
		n: 'pageEditorTabIndicatorColor',
		dv: '#8E90A4',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor ._buttonBar._screenSizes i.fa:hover, .comp.compPageEditor ._buttonBar._screenSizes svg:hover, .comp.compPageEditor ._buttonBar._screenSizes i.fa.active, .comp.compPageEditor ._buttonBar._screenSizes svg.active',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor ._propertyGroup',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorBorderColorStrong',
		dv: '#CCCCCC',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor ._eachProperty',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorBorderColor',
		dv: 'rgba(0, 0, 0, 0.1)',
		cp: 'border-left-color',
		sel: '.comp.compPageEditor ._propBar._right, .comp.compPageEditor ._peSidekickBar',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'border-left-color',
		sel: '.comp.compPageEditor ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorIconColor',
		dv: '#E3E5EA',
		cp: 'color',
		sel: '.comp.compPageEditor ._propBarTabContainer svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'color',
		sel: '.comp.compPageEditor ._filterBar i.fa, .comp.compPageEditor ._compsTree ._treeNode._subComponent._selected, .comp.compPageEditor ._compsTree ._treeNode._subComponent:hover, .comp.compPageEditor ._compsTree ._treeNode._subComponent._selected i.fa, .comp.compPageEditor ._compsTree ._treeNode._subComponent:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._compsTree ._treeNode._selected, .comp.compPageEditor ._compsTree ._treeNode:hover, .comp.compPageEditor ._compsTree ._treeNode._selected i.fa, .comp.compPageEditor ._compsTree ._treeNode:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._compsTree ._treeNode i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'color',
		sel: '.comp.compPageEditor ._compsTree ._treeNode ._treeText ._filter',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorFontColorFaint',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._compsTree ._treeNode:hover ._iconHelperSVG, .comp.compPageEditor ._compsTree ._treeNode._selected ._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor select._peSelect, .comp.compPageEditor ._peSelect, .comp.compPageEditor ._simpleEditorSelect._peSelect, .comp.compPageEditor button:not(:where(._peSidekickBar *)), .comp.compPageEditor ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._filterBar input, .comp.compPageEditor input._peInput, .comp.compPageEditor ._pvExpressionEditor, .comp.compPageEditor textarea._peInput, .comp.compPageEditor select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._addSelector',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorIconColor',
		dv: '#E3E5EA',
		cp: 'color',
		sel: '.comp.compPageEditor ._urlInput ._urlInputIcon svg',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'fill',
		sel: '.comp.compPageEditor ._urlInput ._urlInputIcon svg',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._textValueEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._sideBar ._iconMenu, .comp.compPageEditor ._sideBar ._iconMenu._active, .comp.compPageEditor ._sideBar ._iconMenu:hover',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor button:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor select:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor ._iconMenuOption:hover, .comp.compPageEditor ._popupButtons button:hover, .comp.compPageEditor ._iconMenuBody ._iconMenuOption:hover i.fa, .comp.compPageEditor ._iconMenuBody ._iconMenuOption:hover svg._iconHelperSVG, .comp.compPageEditor ._iconMenu._active svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._sideBar ._iconMenu._active svg._iconHelperSVG, .comp.compPageEditor ._sideBar ._iconMenu:hover svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._sideBar ._iconMenu svg._iconHelperSVG, .comp.compPageEditor i.fa:not(:where(._peSidekickBar *))',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._iconMenu:hover i.fa, .comp.compPageEditor ._iconMenu._active i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorFontColorFaint',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor ._iconMenuBody ._iconMenuOption i.fa, .comp.compPageEditor ._iconMenuBody ._iconMenuOption svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Supporting copy and the resting state of interactive rows.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._iconMenuOption, .comp.compPageEditor ._popupMenuBackground ._popupMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._selectionBar ._iconMenu:hover, .comp.compPageEditor ._buttonBar i.fa.active, .comp.compPageEditor ._buttonBar i.fa:hover, .comp.compPageEditor ._buttonBar svg.active, .comp.compPageEditor ._buttonBar svg:hover',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorIconFill',
		dv: '#E3E5EA',
		cp: 'fill',
		sel: '.comp.compPageEditor ._screenSizes svg path, .comp.compPageEditor ._propLabel._svgButtons svg path, .comp.compPageEditor ._propLabel._svgButtons svg circle, .comp.compPageEditor ._propLabel._svgButtons svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorIconStroke',
		dv: 'rgba(142, 144, 164, 0.5)',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._screenSizes svg path, .comp.compPageEditor ._propLabel._svgButtons svg path, .comp.compPageEditor ._propLabel._svgButtons svg circle, .comp.compPageEditor ._propLabel._svgButtons svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorIconActiveColor',
		dv: '#8E90A4',
		cp: 'fill',
		sel: '.comp.compPageEditor ._screenSizes svg.active path, .comp.compPageEditor ._propLabel._svgButtons.active svg path, .comp.compPageEditor ._propLabel._svgButtons.active svg circle, .comp.compPageEditor ._propLabel._svgButtons.active svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorIconActiveColor',
		dv: '#8E90A4',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._screenSizes svg.active path, .comp.compPageEditor ._propLabel._svgButtons.active svg path, .comp.compPageEditor ._propLabel._svgButtons.active svg circle, .comp.compPageEditor ._propLabel._svgButtons.active svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'fill',
		sel: '.comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover path, .comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover circle, .comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover rect, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active path, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active rect, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active circle',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover path, .comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover circle, .comp.compPageEditor ._propLabel._svgButtons .svgContainer:hover rect, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active path, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active rect, .comp.compPageEditor ._propLabel._svgButtons .svgContainer.active circle',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl ._control',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._scaleControlContainer ._scaleControl ._control svg',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._microToggle2._withText',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._microToggle2._on',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor ._simpleEditorPixelSize ._inputDropdownContainer input',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorInputWithIcon, .comp.compPageEditor ._simpleEditorSelect, .comp.compPageEditor ._simpleEditorInput',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'fill',
		sel: '.comp.compPageEditor ._page_Selector ._simpleEditorSelect path',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._add_page_btn_container button._add_page_btn',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch::placeholder, .comp.compPageEditor ._simpleEditorSelect ._selectedOption._placeholder, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered, .comp.compPageEditor ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._selected',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorIconFill',
		dv: '#E3E5EA',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg path, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorIconStroke',
		dv: 'rgba(142, 144, 164, 0.5)',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg path, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorIcons._bground ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color1',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color1, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color1',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 2',
		de: 'Second slot of the layout illustration palette.',
		n: 'pageEditorIconPaletteTwo',
		dv: '#007962',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color2',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 2',
		de: 'Second slot of the layout illustration palette.',
		n: 'pageEditorIconPaletteTwo',
		dv: '#007962',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color2, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color2',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 3',
		de: 'Third slot of the layout illustration palette.',
		n: 'pageEditorIconPaletteThree',
		dv: '#2A69B5',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color3',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 3',
		de: 'Third slot of the layout illustration palette.',
		n: 'pageEditorIconPaletteThree',
		dv: '#2A69B5',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._highlight._color3, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._highlight._color3',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorIconFill',
		dv: '#E3E5EA',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._lowlight',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorIconStroke',
		dv: 'rgba(142, 144, 164, 0.5)',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorIcons ._eachIcon svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon svg line._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon:hover svg line._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg path._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg circle._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg rect._lowlight, .comp.compPageEditor ._simpleEditorIcons ._eachIcon._active svg line._lowlight',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Warning Color',
		de: 'Colour of the starred or overridden property marker.',
		n: 'pageEditorWarningColor',
		dv: '#FFCC00',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditor._warning',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorBigSelector ._searchResult ._searchResultItem:hover ._animationName',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle._gradient',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._dropdownToggle, .comp.compPageEditor ._dropdownToggle._open',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorIconInverseColor',
		dv: '#FFFFFF',
		cp: 'fill',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorIconInverseColor',
		dv: '#FFFFFF',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorGroupContent ._editorLine ._label',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._detailStyleEditor ._header',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._simpleLabel',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorSurface',
		dv: '#F9F9F9',
		cp: 'fill',
		sel: '.comp.compPageEditor ._positionKnob #background',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorIconInverseColor',
		dv: '#FFFFFF',
		cp: 'fill',
		sel: '.comp.compPageEditor ._positionKnob #knob',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorIconFill',
		dv: '#E3E5EA',
		cp: 'fill',
		sel: '.comp.compPageEditor ._positionKnob #left, .comp.compPageEditor ._positionKnob #top, .comp.compPageEditor ._positionKnob #right, .comp.compPageEditor ._positionKnob #bottom',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'fill',
		sel: '.comp.compPageEditor ._positionKnob._left #left, .comp.compPageEditor ._positionKnob._top #top, .comp.compPageEditor ._positionKnob._right #right, .comp.compPageEditor ._positionKnob._bottom #bottom',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._spacingEditor ._label',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'color',
		sel: '.comp.compPageEditor ._spacingEditor ._label._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._spacingEditor ._changer ._header, .comp.compPageEditor ._spacingEditor ._value',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._spacingEditor ._value._default',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorFontColorFaint',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor ._buttonBar._screenSizes i.fa:hover, .comp.compPageEditor ._buttonBar._screenSizes svg:hover, .comp.compPageEditor ._buttonBar._screenSizes i.fa.active, .comp.compPageEditor ._buttonBar._screenSizes svg.active',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._screenSizes i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._iframeHeader',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._eachSelectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorFontColorFaint',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor ._dragBar',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorIconActiveColor',
		dv: '#8E90A4',
		cp: 'fill',
		sel: '.comp.compPageEditor ._tabBar svg path, .comp.compPageEditor ._tabBar svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'fill',
		sel: '.comp.compPageEditor ._tabBar svg:hover path, .comp.compPageEditor ._tabBar svg.active path, .comp.compPageEditor ._tabBar svg:hover rect, .comp.compPageEditor ._tabBar svg.active rect',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._styleButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._styleButtonContainer button:hover, .comp.compPageEditor ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorIconInverseColor',
		dv: '#FFFFFF',
		cp: 'fill',
		sel: '.comp.compPageEditor ._styleButtonContainer button:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorIconFill',
		dv: '#E3E5EA',
		cp: 'fill',
		sel: '.comp.compPageEditor ._styleButtonContainer button svg path',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorIconColor',
		dv: '#E3E5EA',
		cp: 'color',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._propLabel',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._description',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._tooltip:hover::after',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._propertyGroupHeader',
		np: true,
	},
	{
		gn: 'Page Editor Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorAccentColor',
		dv: '#52BD94',
		cp: 'fill',
		sel: '.comp.compPageEditor ._propertyGroupHeaderStar',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorIconColor',
		dv: '#E3E5EA',
		cp: 'color',
		sel: '.comp.compPageEditor ._propertyGroupHeader i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._propertyGroup ._detailsSwitchEditor',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._propertyGroup ._detailsSwitchEditor._open, .comp.compPageEditor ._propertyGroup ._detailsSwitchEditor:hover',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorFontColorFaint',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor ._eachProperty i.fa._controlIcons',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formButton ._backButton',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorHeader',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorSubHeader, .comp.compPageEditor ._popupContainer._formEditor ._formEditorContent ._formEditorFilesTitle, .comp.compPageEditor ._formEditorOptionsPagination ._paginationPrev, .comp.compPageEditor ._formEditorOptionsPagination ._paginationPages, .comp.compPageEditor ._formEditorOptionsPagination ._paginationNext',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorFontColorSecondary',
		dv: '#555555',
		cp: 'color',
		sel: '.comp.compPageEditor ._paginationContainer ._prevButton, .comp.compPageEditor ._paginationContainer ._nextButton',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._paginationContainer ._pageButtonActive',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionName',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldsAndButtonsTitle',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupContainer._formEditor ._formFieldAndButton ._formFieldAndButtonTitle',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupBackground ._popupContainer ._pathParts span._clickable:hover',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._tabContainer ._tab',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._tabContainer ._tab:hover',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorIconColor',
		dv: '#E3E5EA',
		cp: 'color',
		sel: '.comp.compPageEditor ._tab._inActive svg',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._topRightBarGrid button._save, .comp.compPageEditor ._topRightBarGrid button._publish',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorFontColorMuted',
		dv: 'rgba(0, 0, 0, 0.4)',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compMenuSearch::placeholder',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorFontColor',
		dv: '#000000',
		cp: 'color',
		sel: '.comp.compPageEditor ._tutorialCloser, .comp.compPageEditor ._tutorialHeader, .comp.compPageEditor ._tutorialHeader h3',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'fill',
		sel: '.comp.compPageEditor ._tutorialIcon:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorIconPaletteOne',
		dv: '#3A8BED',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._tutorialIcon:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorFontColorInverse',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._youtubeButton',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorFontColorDisabled',
		dv: '#D2D3DB',
		cp: 'color',
		sel: '.comp.compPageEditor ._tutorialDescription',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem.active svg, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover svg, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem.active i.fa, .comp.compPageEditor ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Device Icon Gradient Start',
		de: 'First stop of the gradient filling an enabled device toggle.',
		n: 'pageEditorDeviceIconGradientStart',
		dv: '#43B2FF',
		cp: 'stop-color',
		sel: '.comp.compPageEditor ._control._device svg linearGradient stop:first-child',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Device Icon Gradient End',
		de: 'Second stop of the device toggle gradient.',
		n: 'pageEditorDeviceIconGradientEnd',
		dv: '#52BD94',
		cp: 'stop-color',
		sel: '.comp.compPageEditor ._control._device svg linearGradient stop:last-child',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Device Icon Stroke',
		de: 'Outline of the device toggles. Scoped to path[stroke] so the gradient body is untouched.',
		n: 'pageEditorDeviceIconStroke',
		dv: '#C2C3CE',
		cp: 'stroke',
		sel: '.comp.compPageEditor ._control._device svg path[stroke]',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Root Font Color',
		de: 'Text colour on the editor root, inherited by everything that sets none of its own -- the component tree labels among them. Opt-in: emits nothing until set.',
		n: 'pageEditorRootFontColor',
		cp: 'color',
		sel: '.comp.compPageEditor',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Top Bar Control Background',
		de: 'Ground behind the page selector in the top bar. Opt-in: without it the selector is transparent, which reads as unstyled beside the URL pill.',
		n: 'pageEditorTopBarControlBackground',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._page_Selector ._simpleEditorSelect, .comp.compPageEditor ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Depth',
		dn: 'Top Bar Control Shadow',
		de: 'Elevation for the page selector, so it reads as a control on a same-coloured bar.',
		n: 'pageEditorTopBarControlShadow',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor ._page_Selector ._simpleEditorSelect, .comp.compPageEditor ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Root Icon Fill',
		de: 'Fill for inline SVG shapes that declare none of their own. Opt-in: without it they fall back to black, which disappears on a dark chrome.',
		n: 'pageEditorRootIconFill',
		cp: 'fill',
		sel: '.comp.compPageEditor',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Info Badge Font Color',
		de: 'The letter inside the small round info badge beside a property label.',
		n: 'pageEditorBadgeFontColor',
		dv: '#FFFFFF',
		cp: 'color',
		sel: '.comp.compPageEditor ._description',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Input Font Color',
		de: 'Text inside inputs, textareas and selects that no other rule colours. Opt-in: without it they fall back to the UA black, invisible on a dark chrome.',
		n: 'pageEditorInputFontColor',
		cp: 'color',
		sel: '.comp.compPageEditor input:not(:where(._peSidekickBar *)), .comp.compPageEditor textarea:not(:where(._peSidekickBar *)), .comp.compPageEditor select:not(:where(._peSidekickBar *))',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Toggle Track',
		de: 'Ground of an off micro toggle.',
		n: 'pageEditorToggleTrack',
		dv: '#F9F9F9',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2, .comp.compPageEditor ._pvEditor ._microToggle',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Toggle Track On',
		de: 'Ground of an on micro toggle that is not accent-filled.',
		n: 'pageEditorToggleTrackOn',
		dv: '#E8EAEB',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._pvEditor ._microToggle._on',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Toggle Thumb',
		de: 'The knob of an off micro toggle. Must read against its own track.',
		n: 'pageEditorToggleThumb',
		dv: '#CCCCCC',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2::before, .comp.compPageEditor ._pvEditor ._microToggle::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Toggle Thumb On',
		de: 'The knob once on, sitting on the accent-filled track.',
		n: 'pageEditorToggleThumbOn',
		dv: '#FFFFFF',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._microToggle2._on::before, .comp.compPageEditor ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Surface',
		dn: 'Canvas Background',
		de: 'The mat around the page being edited. Opt-in: without it the canvas simply shows the editor surface behind it.',
		n: 'pageEditorCanvasBackground',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iframeContainer',
		np: true,
	},
	{
		gn: 'Page Editor Type',
		dn: 'Label Letter Spacing',
		de: 'Opt-in tracking on the small property labels. Emits nothing until set.',
		n: 'pageEditorLabelLetterSpacing',
		cp: 'letter-spacing',
		sel: '.comp.compPageEditor ._propLabel, .comp.compPageEditor ._simpleEditorGroupTitle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorDarkShadowMedium',
		dv: '0px 2px 6px 0px rgba(0, 0, 0, 0.6)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._propBar._right',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorDarkShadowLarge',
		dv: '0 18px 40px 0 rgba(0, 0, 0, 0.55), 0 6px 16px 0 rgba(0, 0, 0, 0.45)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._pvExpressionEditor ._pathsList',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorDarkShadowSmall',
		dv: '0px 1px 3px 0px rgba(0, 0, 0, 0.55)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._urlInput._peInput, .comp.compPageEditor._dark ._iconMenuBody, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorDarkShadowMedium',
		dv: '0px 2px 6px 0px rgba(0, 0, 0, 0.6)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon:hover, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon._active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorDarkShadowSmall',
		dv: '0px 1px 3px 0px rgba(0, 0, 0, 0.55)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._dropdownContent, .comp.compPageEditor._dark ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorDarkShadowLarge',
		dv: '0 18px 40px 0 rgba(0, 0, 0, 0.55), 0 6px 16px 0 rgba(0, 0, 0, 0.45)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._detailStyleEditor',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorDarkShadowMedium',
		dv: '0px 2px 6px 0px rgba(0, 0, 0, 0.6)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._changer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorDarkShadowSmall',
		dv: '0px 1px 3px 0px rgba(0, 0, 0, 0.55)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorDarkShadowMedium',
		dv: '0px 2px 6px 0px rgba(0, 0, 0, 0.6)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorDarkShadowLarge',
		dv: '0 18px 40px 0 rgba(0, 0, 0, 0.55), 0 6px 16px 0 rgba(0, 0, 0, 0.45)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._codeEditorContent, .comp.compPageEditor._dark ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorDarkShadowSmall',
		dv: '0px 1px 3px 0px rgba(0, 0, 0, 0.55)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow M',
		de: 'Hovered controls and floating toolbars.',
		n: 'pageEditorDarkShadowMedium',
		dv: '0px 2px 6px 0px rgba(0, 0, 0, 0.6)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorEachOption:hover ._formEditorEachOptionPreview, .comp.compPageEditor._dark ._popupMenuContainer._compMenu._show._compMenuRight, .comp.compPageEditor._dark ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow S',
		de: 'Resting elevation on chips and inputs.',
		n: 'pageEditorDarkShadowSmall',
		dv: '0px 1px 3px 0px rgba(0, 0, 0, 0.55)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._tutorialTooltipPanel',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorDarkShadowLarge',
		dv: '0 18px 40px 0 rgba(0, 0, 0, 0.55), 0 6px 16px 0 rgba(0, 0, 0, 0.45)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._addSelector',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._sideBar ._iconMenu',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorDarkInverseBackground',
		dv: '#F5F5F4',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._sideBar ._iconMenu._active, .comp.compPageEditor._dark ._sideBar ._iconMenu:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._buttonBar i.fa, .comp.compPageEditor._dark ._buttonBar svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._iframeHeader._screenSizes svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorDarkSelectedBackground',
		dv: 'rgba(245, 158, 11, 0.14)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._scaleControlContainer ._scaleControl',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._add_page_btn_container button._add_page_btn',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._simpleEditorBigSelector ._searchResult ._searchResultItem ._animationIcon',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupTitle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroup ._simpleEditorGroupContent',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Tutorial Banner Gradient',
		de: 'The two-tone banner at the head of the tutorial panel.',
		n: 'pageEditorDarkHeaderGradient',
		dv: 'linear-gradient(150deg, #FBBF24 13.39%, #B45309 86.61%)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._simpleEditorAngle ._angleTrack::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._value, .comp.compPageEditor._dark ._tabBar, .comp.compPageEditor._dark ._styleButtonContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorDarkInverseBackground',
		dv: '#F5F5F4',
		cp: 'background',
		sel: '.comp.compPageEditor._dark .commonTriStateCheckbox::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._formEditorBottomBorder, .comp.compPageEditor._dark ._paginationContainer ._pageButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._paginationContainer ._pageButtonActive',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionPreview, .comp.compPageEditor._dark ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldAndButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Overlay Scrim',
		de: 'The dimming layer behind a modal popup.',
		n: 'pageEditorDarkScrimBackground',
		dv: 'rgba(0, 0, 0, 0.62)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupBackground',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Save Button Gradient',
		de: 'Fill of the Save button, the editor primary action.',
		n: 'pageEditorDarkSaveGradient',
		dv: 'linear-gradient(180deg, #FBBF24 0%, #F59E0B 100%)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._topRightBarGrid button._save',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Publish Button Gradient',
		de: 'Fill of the Publish button. Its only job is to stay distinguishable from Save.',
		n: 'pageEditorDarkPublishGradient',
		dv: 'linear-gradient(180deg, #D97706 0%, #B45309 100%)',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._topRightBarGrid button._publish',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorDarkSurfaceStrong',
		dv: '#2E2E33',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._toggleButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._toggleButton::after',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._tutorialTooltipPanel',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Inverse Background',
		de: 'Grounds that invert against the panel: the active side bar icon, the video button.',
		n: 'pageEditorDarkInverseBackground',
		dv: '#F5F5F4',
		cp: 'background',
		sel: '.comp.compPageEditor._dark ._youtubeButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._dndGrid',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._topBarGrid, .comp.compPageEditor._dark ._sideBar, .comp.compPageEditor._dark ._propBar, .comp.compPageEditor._dark ._peSidekickBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorDarkSelectedBackground',
		dv: 'rgba(245, 158, 11, 0.14)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._compsTree ._treeNode._selected, .comp.compPageEditor._dark ._compsTree ._treeNode:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark select._peSelect, .comp.compPageEditor._dark ._peSelect, .comp.compPageEditor._dark ._simpleEditorSelect._peSelect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark button:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._filterBar input, .comp.compPageEditor._dark input._peInput, .comp.compPageEditor._dark ._pvExpressionEditor, .comp.compPageEditor._dark textarea._peInput, .comp.compPageEditor._dark select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark textarea._peInput::-webkit-scrollbar-thumb',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvExpressionEditor ._pathsList',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvExpressionEditor ._pathsList ._path:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._textValueEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._sideBar ._seperator',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorDarkSelectedBackground',
		dv: 'rgba(245, 158, 11, 0.14)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark button:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark select:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark ._iconMenuOption:hover, .comp.compPageEditor._dark ._popupButtons button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iconMenuBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorDarkSelectedBackground',
		dv: 'rgba(245, 158, 11, 0.14)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iconMenu._active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iconMenu:hover, .comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption:hover, .comp.compPageEditor._dark ._popupMenuBackground ._popupMenuItem:hover, .comp.compPageEditor._dark ._tabContainer ._tab:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._buttonBar._lightBackground',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Selected Row Background',
		de: 'Tint behind the selected component in the tree and the selected search result.',
		n: 'pageEditorDarkSelectedBackground',
		dv: 'rgba(245, 158, 11, 0.14)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._buttonBar i.fa.active, .comp.compPageEditor._dark ._buttonBar i.fa:hover, .comp.compPageEditor._dark ._buttonBar svg.active, .comp.compPageEditor._dark ._buttonBar svg:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._scaleControlContainer ._scaleControl ._control._text, .comp.compPageEditor._dark ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorDarkSurfaceStrong',
		dv: '#2E2E33',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on, .comp.compPageEditor._dark ._microToggle2:hover::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor._dark ._simpleEditorInputWithIcon, .comp.compPageEditor._dark ._simpleEditorSelect, .comp.compPageEditor._dark ._simpleEditorInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered, .comp.compPageEditor._dark ._simpleEditorIcons._bground',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon:hover, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon._active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorBigSelector ._searchBox',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._dropdownToggle._open, .comp.compPageEditor._dark ._dropdownContent',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._dropdownContent ._eachIcon:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorAngle, .comp.compPageEditor._dark ._simpleEditorButtonBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._detailStyleEditor, .comp.compPageEditor._dark ._spacingEditor ._changer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._changer ._header',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._square, .comp.compPageEditor._dark ._spacingEditor ._circle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._square._hasValue, .comp.compPageEditor._dark ._spacingEditor ._circle._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._screenSizes:hover, .comp.compPageEditor._dark ._iframeHeader, .comp.compPageEditor._dark ._selectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorDarkSurfaceStrong',
		dv: '#2E2E33',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._previewModeCloser',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle::before, .comp.compPageEditor._dark ._pvEditor ._microToggle._on',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorDarkSurfaceStrong',
		dv: '#2E2E33',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._propValue._padding',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._description',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._tooltip:hover::after, .comp.compPageEditor._dark ._pillTag, .comp.compPageEditor._dark ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iconSelectionButton.active, .comp.compPageEditor._dark ._eachProperty i.fa._controlIcons',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._codeEditorContent',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._codeEditorContent ._codeEditorHeader',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Soft Background',
		de: 'A translucent accent fill used behind hovered accent controls.',
		n: 'pageEditorDarkAccentSoftBackground',
		dv: 'rgba(245, 158, 11, 0.85)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._addPropertyButtonContainer button:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Strong Chip Surface',
		de: 'Drag handles, resize bars and pressed chips.',
		n: 'pageEditorDarkSurfaceStrong',
		dv: '#2E2E33',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._iconSelectionDisplay ._eachIcon:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._eachIcon input, .comp.compPageEditor._dark ._popupBackground ._popupContainer ._pathParts span._clickable:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer, .comp.compPageEditor._dark ._popupMenuBackground ._popupMenuContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._tabContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compMenuSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._sectionThumb:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark button:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._filterBar input, .comp.compPageEditor._dark ._scaleControlContainer ._scaleControl',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Heavy Border Color',
		de: 'The darkest rule in the editor, the pill toggle track.',
		n: 'pageEditorDarkBorderColorHeavy',
		dv: 'rgba(255, 255, 255, 0.30)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Border Color',
		de: 'The darker rim drawn around a filled accent control.',
		n: 'pageEditorDarkAccentBorderColor',
		dv: '#B45309',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._microToggle2:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Heavy Border Color',
		de: 'The darkest rule in the editor, the pill toggle track.',
		n: 'pageEditorDarkBorderColorHeavy',
		dv: 'rgba(255, 255, 255, 0.30)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._microToggle2::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Border Color',
		de: 'The darker rim drawn around a filled accent control.',
		n: 'pageEditorDarkAccentBorderColor',
		dv: '#B45309',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on, .comp.compPageEditor._dark ._microToggle2:hover::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Inverse Border Color',
		de: 'A border drawn on an inverted ground.',
		n: 'pageEditorDarkBorderColorInverse',
		dv: '#17171A',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorBigSelector ._searchBox',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._dropdownContent',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Highlight Border Color',
		de: 'The bright ring around the angle dial in the style editor.',
		n: 'pageEditorDarkHighlightBorderColor',
		dv: '#F59E0B',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupContent ._simpleEditorAngle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._detailStyleEditor',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Button Border Color',
		de: 'The ring around a selectable icon button.',
		n: 'pageEditorDarkIconBorderColor',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor._margin',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor._margin._hasValue, .comp.compPageEditor._dark ._spacingEditor ._padding._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Button Border Color',
		de: 'The ring around a selectable icon button.',
		n: 'pageEditorDarkIconBorderColor',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._padding',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._iframeHeader',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._propValue._padding',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._tooltip:hover::after, .comp.compPageEditor._dark ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._codeEditorContent ._iconMenuSeperator',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Border Color',
		de: 'Outline around a focused or highlighted informational control.',
		n: 'pageEditorDarkInfoBorderColor',
		dv: 'rgba(96, 165, 250, 0.8)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorEachOption:hover ._formEditorEachOptionPreview',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._jsonEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._eachIcon._upload',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._popupMenuBackground ._popupMenuContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Focus Border Color',
		de: 'The rim on a focused search box.',
		n: 'pageEditorDarkFocusBorderColor',
		dv: '#F59E0B',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compMenuSearch:focus',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._tutorialTooltipPanel, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._sectionThumb, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem.active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor._dark ._sideBar, .comp.compPageEditor._dark ._propBar._left',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Dotted Guide Color',
		de: 'The dotted guide between spacing-editor cells.',
		n: 'pageEditorDarkBorderColorDotted',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor._dark ._compsTree:hover ._treeNodeLevel, .comp.compPageEditor._dark ._compsTree:hover ._treeNode._subComponent:hover ._treeNodeLevel, .comp.compPageEditor._dark ._compsTree:hover ._treeNode._subComponent._selected ._treeNodeLevel, .comp.compPageEditor._dark ._compsTree ._treeNodeLevel._lastOpened',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer ._seperator',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-right-color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._left, .comp.compPageEditor._dark ._popupMenuContainer._compMenu._show._compMenuRight',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._topBarGrid, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._options_divider, .comp.compPageEditor._dark ._detailStyleEditor ._header, .comp.compPageEditor._dark ._spacingEditor ._changer ._header',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Tab Indicator Color',
		de: 'The underline beneath the selected tab.',
		n: 'pageEditorDarkTabIndicatorColor',
		dv: '#F59E0B',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._buttonBar._screenSizes i.fa:hover, .comp.compPageEditor._dark ._buttonBar._screenSizes svg:hover, .comp.compPageEditor._dark ._buttonBar._screenSizes i.fa.active, .comp.compPageEditor._dark ._buttonBar._screenSizes svg.active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._propertyGroup',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._eachProperty',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-left-color',
		sel: '.comp.compPageEditor._dark ._propBar._right',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'border-left-color',
		sel: '.comp.compPageEditor._dark ._popupMenuBackground ._contextMenu',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propBarTabContainer svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._filterBar i.fa, .comp.compPageEditor._dark ._compsTree ._treeNode._subComponent._selected, .comp.compPageEditor._dark ._compsTree ._treeNode._subComponent:hover, .comp.compPageEditor._dark ._compsTree ._treeNode._subComponent._selected i.fa, .comp.compPageEditor._dark ._compsTree ._treeNode._subComponent:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._compsTree ._treeNode._selected, .comp.compPageEditor._dark ._compsTree ._treeNode:hover, .comp.compPageEditor._dark ._compsTree ._treeNode._selected i.fa, .comp.compPageEditor._dark ._compsTree ._treeNode:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._compsTree ._treeNode i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._compsTree ._treeNode ._treeText ._filter',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorDarkFontColorFaint',
		dv: 'rgba(245, 245, 244, 0.46)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._compsTree ._treeNode:hover ._iconHelperSVG, .comp.compPageEditor._dark ._compsTree ._treeNode._selected ._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark select._peSelect, .comp.compPageEditor._dark ._peSelect, .comp.compPageEditor._dark ._simpleEditorSelect._peSelect, .comp.compPageEditor._dark button:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark ._popupButtons button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._filterBar input, .comp.compPageEditor._dark input._peInput, .comp.compPageEditor._dark ._pvExpressionEditor, .comp.compPageEditor._dark textarea._peInput, .comp.compPageEditor._dark select._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._addSelector',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._urlInput ._urlInputIcon svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._urlInput ._urlInputIcon svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._textValueEditorContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._sideBar ._iconMenu, .comp.compPageEditor._dark ._sideBar ._iconMenu._active, .comp.compPageEditor._dark ._sideBar ._iconMenu:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark button:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark select:hover:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark ._iconMenuOption:hover, .comp.compPageEditor._dark ._popupButtons button:hover, .comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption:hover i.fa, .comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption:hover svg._iconHelperSVG, .comp.compPageEditor._dark ._iconMenu._active svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._sideBar ._iconMenu._active svg._iconHelperSVG, .comp.compPageEditor._dark ._sideBar ._iconMenu:hover svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._sideBar ._iconMenu svg._iconHelperSVG, .comp.compPageEditor._dark i.fa:not(:where(._peSidekickBar *))',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconMenu:hover i.fa, .comp.compPageEditor._dark ._iconMenu._active i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorDarkFontColorFaint',
		dv: 'rgba(245, 245, 244, 0.46)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption i.fa, .comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Supporting copy and the resting state of interactive rows.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconMenuOption, .comp.compPageEditor._dark ._popupMenuBackground ._popupMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._selectionBar ._iconMenu:hover, .comp.compPageEditor._dark ._buttonBar i.fa.active, .comp.compPageEditor._dark ._buttonBar i.fa:hover, .comp.compPageEditor._dark ._buttonBar svg.active, .comp.compPageEditor._dark ._buttonBar svg:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorDarkIconFill',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._screenSizes svg path, .comp.compPageEditor._dark ._propLabel._svgButtons svg path, .comp.compPageEditor._dark ._propLabel._svgButtons svg circle, .comp.compPageEditor._dark ._propLabel._svgButtons svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorDarkIconStroke',
		dv: 'rgba(245, 245, 244, 0.40)',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._screenSizes svg path, .comp.compPageEditor._dark ._propLabel._svgButtons svg path, .comp.compPageEditor._dark ._propLabel._svgButtons svg circle, .comp.compPageEditor._dark ._propLabel._svgButtons svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorDarkIconActiveColor',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._screenSizes svg.active path, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg path, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg circle, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorDarkIconActiveColor',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._screenSizes svg.active path, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg path, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg circle, .comp.compPageEditor._dark ._propLabel._svgButtons.active svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover path, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover circle, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover rect, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active path, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active rect, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active circle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover path, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover circle, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer:hover rect, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active path, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active rect, .comp.compPageEditor._dark ._propLabel._svgButtons .svgContainer.active circle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._scaleControlContainer ._scaleControl ._control',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._scaleControlContainer ._scaleControl ._control svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._microToggle2',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._microToggle2._withText',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorPixelSize ._inputDropdownContainer, .comp.compPageEditor._dark ._simpleEditorPixelSize ._inputDropdownContainer input',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorInputWithIcon, .comp.compPageEditor._dark ._simpleEditorSelect, .comp.compPageEditor._dark ._simpleEditorInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._page_Selector ._simpleEditorSelect path',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._add_page_btn_container button._add_page_btn',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody input._simpleEditorDropdownSearch::placeholder, .comp.compPageEditor._dark ._simpleEditorSelect ._selectedOption._placeholder, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered, .comp.compPageEditor._dark ._simpleEditorSelect ._simpleEditorDropdownBody ._simpleEditorDropdownOption._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorDarkIconFill',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorDarkIconStroke',
		dv: 'rgba(245, 245, 244, 0.40)',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorIcons._bground ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color1',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color1, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color1',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 2',
		de: 'Second slot of the layout illustration palette.',
		n: 'pageEditorDarkIconPaletteTwo',
		dv: '#F59E0B',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color2',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 2',
		de: 'Second slot of the layout illustration palette.',
		n: 'pageEditorDarkIconPaletteTwo',
		dv: '#F59E0B',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color2, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color2',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 3',
		de: 'Third slot of the layout illustration palette.',
		n: 'pageEditorDarkIconPaletteThree',
		dv: '#60A5FA',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color3',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 3',
		de: 'Third slot of the layout illustration palette.',
		n: 'pageEditorDarkIconPaletteThree',
		dv: '#60A5FA',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._highlight._color3, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._highlight._color3',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorDarkIconFill',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._lowlight',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Stroke',
		de: 'The outline of a resting icon, deliberately darker than its fill.',
		n: 'pageEditorDarkIconStroke',
		dv: 'rgba(245, 245, 244, 0.40)',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon svg line._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon:hover svg line._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg path._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg circle._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg rect._lowlight, .comp.compPageEditor._dark ._simpleEditorIcons ._eachIcon._active svg line._lowlight',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Warning Color',
		de: 'Colour of the starred or overridden property marker.',
		n: 'pageEditorDarkWarningColor',
		dv: '#FBBF24',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditor._warning',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorBigSelector ._searchResult ._searchResultItem:hover ._animationName',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._dropdownToggle, .comp.compPageEditor._dark ._dropdownToggle._open',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorDarkIconInverseColor',
		dv: '#17171A',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorDarkIconInverseColor',
		dv: '#17171A',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg path, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg circle, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg rect, .comp.compPageEditor._dark ._simpleEditorGroupTitle._gradient ._simpleEditorIcons ._eachIcon svg line',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorGroupContent ._editorLine ._label',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorButtonBar ._simpleButtonBarButton._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._detailStyleEditor ._header',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleLabel',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._positionKnob #background',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorDarkIconInverseColor',
		dv: '#17171A',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._positionKnob #knob',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorDarkIconFill',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._positionKnob #left, .comp.compPageEditor._dark ._positionKnob #top, .comp.compPageEditor._dark ._positionKnob #right, .comp.compPageEditor._dark ._positionKnob #bottom',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._positionKnob._left #left, .comp.compPageEditor._dark ._positionKnob._top #top, .comp.compPageEditor._dark ._positionKnob._right #right, .comp.compPageEditor._dark ._positionKnob._bottom #bottom',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._label',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._label._hasValue',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._changer ._header, .comp.compPageEditor._dark ._spacingEditor ._value',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._spacingEditor ._value._default',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorDarkFontColorFaint',
		dv: 'rgba(245, 245, 244, 0.46)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._buttonBar._screenSizes i.fa:hover, .comp.compPageEditor._dark ._buttonBar._screenSizes svg:hover, .comp.compPageEditor._dark ._buttonBar._screenSizes i.fa.active, .comp.compPageEditor._dark ._buttonBar._screenSizes svg.active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._screenSizes i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iframeHeader',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._eachSelectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorDarkFontColorFaint',
		dv: 'rgba(245, 245, 244, 0.46)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._dragBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorDarkIconActiveColor',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._tabBar svg path, .comp.compPageEditor._dark ._tabBar svg rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._tabBar svg:hover path, .comp.compPageEditor._dark ._tabBar svg.active path, .comp.compPageEditor._dark ._tabBar svg:hover rect, .comp.compPageEditor._dark ._tabBar svg.active rect',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button:hover, .comp.compPageEditor._dark ._addArrayItemButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Inverse Icon Color',
		de: 'Icon fill and stroke on an accent or inverted ground.',
		n: 'pageEditorDarkIconInverseColor',
		dv: '#17171A',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Fill',
		de: 'The flat fill of a resting outline icon.',
		n: 'pageEditorDarkIconFill',
		dv: 'rgba(255, 255, 255, 0.14)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._styleButtonContainer button svg path',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propLabel',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._description',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tooltip:hover::after',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propertyGroupHeader',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._propertyGroupHeaderStar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propertyGroupHeader i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propertyGroup ._detailsSwitchEditor',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._propertyGroup ._detailsSwitchEditor._open, .comp.compPageEditor._dark ._propertyGroup ._detailsSwitchEditor:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Faint Font Color',
		de: 'The faintest readable tier: tree guides, empty-state copy.',
		n: 'pageEditorDarkFontColorFaint',
		dv: 'rgba(245, 245, 244, 0.46)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._eachProperty i.fa._controlIcons',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._addPropertyButtonContainer button',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formButton ._backButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._generateButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._formEditorHeader',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._formEditorSubHeader, .comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorContent ._formEditorFilesTitle, .comp.compPageEditor._dark ._formEditorOptionsPagination ._paginationPrev, .comp.compPageEditor._dark ._formEditorOptionsPagination ._paginationPages, .comp.compPageEditor._dark ._formEditorOptionsPagination ._paginationNext',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._paginationContainer ._prevButton, .comp.compPageEditor._dark ._paginationContainer ._nextButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._paginationContainer ._pageButtonActive',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formEditorEachOption ._formEditorEachOptionName',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formFieldsAndButtons ._formFieldsAndButtonsTitle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupContainer._formEditor ._formFieldAndButton ._formFieldAndButtonTitle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupBackground ._popupContainer ._pathParts span._clickable:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tabContainer ._tab',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tabContainer ._tab:hover',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tabContainer ._tab._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tab._inActive svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._topRightBarGrid button._save, .comp.compPageEditor._dark ._topRightBarGrid button._publish',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compMenuSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compMenuSearch::placeholder',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tutorialCloser, .comp.compPageEditor._dark ._tutorialHeader, .comp.compPageEditor._dark ._tutorialHeader h3',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark ._tutorialIcon:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Illustration Color 1',
		de: 'First slot of the three-colour palette used by the layout illustration icons.',
		n: 'pageEditorDarkIconPaletteOne',
		dv: '#FBBF24',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._tutorialIcon:hover svg path',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Inverse Font Color',
		de: 'Text drawn on an accent or inverted ground.',
		n: 'pageEditorDarkFontColorInverse',
		dv: '#17171A',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._youtubeButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Disabled Font Color',
		de: 'Values showing an inherited default rather than a set one.',
		n: 'pageEditorDarkFontColorDisabled',
		dv: 'rgba(245, 245, 244, 0.32)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._tutorialDescription',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'One step lighter than primary: group headers, tab labels.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem.active svg, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover svg, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem.active i.fa, .comp.compPageEditor._dark ._popupMenuContainer._compMenu ._compList ._compMenuItem:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Device Icon Gradient Start',
		de: 'First stop of the gradient filling an enabled device toggle.',
		n: 'pageEditorDarkDeviceIconGradientStart',
		dv: '#FBBF24',
		cp: 'stop-color',
		sel: '.comp.compPageEditor._dark ._control._device svg linearGradient stop:first-child',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Device Icon Gradient End',
		de: 'Second stop of the device toggle gradient.',
		n: 'pageEditorDarkDeviceIconGradientEnd',
		dv: '#F59E0B',
		cp: 'stop-color',
		sel: '.comp.compPageEditor._dark ._control._device svg linearGradient stop:last-child',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Device Icon Stroke',
		de: 'Outline of the device toggles. Scoped to path[stroke] so the gradient body is untouched.',
		n: 'pageEditorDarkDeviceIconStroke',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'stroke',
		sel: '.comp.compPageEditor._dark ._control._device svg path[stroke]',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Root Font Color',
		de: 'Text colour on the editor root, inherited by everything that sets none of its own -- the component tree labels among them. Opt-in: emits nothing until set.',
		n: 'pageEditorDarkRootFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Top Bar Control Background',
		de: 'Ground behind the page selector in the top bar. Opt-in: without it the selector is transparent, which reads as unstyled beside the URL pill.',
		n: 'pageEditorDarkTopBarControlBackground',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._page_Selector ._simpleEditorSelect, .comp.compPageEditor._dark ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Top Bar Control Shadow',
		de: 'Elevation for the page selector, so it reads as a control on a same-coloured bar.',
		n: 'pageEditorDarkTopBarControlShadow',
		dv: '0 0 0 1px rgba(255,255,255,.08)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._page_Selector ._simpleEditorSelect, .comp.compPageEditor._dark ._urlInput._peInput',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Root Icon Fill',
		de: 'Fill for inline SVG shapes that declare none of their own. Opt-in: without it they fall back to black, which disappears on a dark chrome.',
		n: 'pageEditorDarkRootIconFill',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'fill',
		sel: '.comp.compPageEditor._dark',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Info Badge Font Color',
		de: 'The letter inside the small round info badge beside a property label.',
		n: 'pageEditorDarkBadgeFontColor',
		dv: 'rgba(245, 245, 244, 0.80)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._description',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Input Font Color',
		de: 'Text inside inputs, textareas and selects that no other rule colours. Opt-in: without it they fall back to the UA black, invisible on a dark chrome.',
		n: 'pageEditorDarkInputFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark input:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark textarea:not(:where(._peSidekickBar *)), .comp.compPageEditor._dark select:not(:where(._peSidekickBar *))',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Toggle Track',
		de: 'Ground of an off micro toggle.',
		n: 'pageEditorDarkToggleTrack',
		dv: 'rgba(255, 255, 255, 0.11)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2, .comp.compPageEditor._dark ._pvEditor ._microToggle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Toggle Track On',
		de: 'Ground of an on micro toggle that is not accent-filled.',
		n: 'pageEditorDarkToggleTrackOn',
		dv: 'rgba(245, 158, 11, 0.45)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle._on',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Toggle Thumb',
		de: 'The knob of an off micro toggle. Must read against its own track.',
		n: 'pageEditorDarkToggleThumb',
		dv: 'rgba(245, 245, 244, 0.55)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2::before, .comp.compPageEditor._dark ._pvEditor ._microToggle::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Toggle Thumb On',
		de: 'The knob once on, sitting on the accent-filled track.',
		n: 'pageEditorDarkToggleThumbOn',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._microToggle2._on::before, .comp.compPageEditor._dark ._pvEditor ._microToggle._on::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Canvas Background',
		de: 'The mat around the page being edited. Opt-in: without it the canvas simply shows the editor surface behind it.',
		n: 'pageEditorDarkCanvasBackground',
		dv: '#08080A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iframeContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: ".comp.compPageEditor._dark input._peInput[type='number']",
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: ".comp.compPageEditor._dark input._peInput[type='number']",
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: ".comp.compPageEditor._dark ._pvExpressionEditor, .comp.compPageEditor._dark input._peInput[type='number']",
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Depth',
		dn: 'Shadow L',
		de: 'Popups and menus lifted clear of the page.',
		n: 'pageEditorDarkShadowLarge',
		dv: '0 18px 40px 0 rgba(0, 0, 0, 0.55), 0 6px 16px 0 rgba(0, 0, 0, 0.45)',
		cp: 'box-shadow',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownOption',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Hover Wash',
		de: 'The translucent wash laid over a row or icon on hover.',
		n: 'pageEditorDarkSurfaceHover',
		dv: 'rgba(255, 255, 255, 0.06)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownOption._hovered',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownOption._selected',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Panel Background',
		de: 'The ground of the top bar, side bar, property bar and every popup.',
		n: 'pageEditorDarkBackground',
		dv: '#17171A',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._simpleEditorDropdownSearchContainer',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Recessed Surface',
		de: 'The ground behind the component tree, editor groups and toolbars.',
		n: 'pageEditorDarkSurface',
		dv: '#0E0E10',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody input._simpleEditorDropdownSearch',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody input._simpleEditorDropdownSearch::placeholder',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-bottom-color',
		sel: '.comp.compPageEditor._dark ._simpleEditorDropdownBody ._options_divider',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Icon Color',
		de: 'Set on the svg itself, so it reaches any part drawn with currentColor.',
		n: 'pageEditorDarkIconColor',
		dv: 'rgba(245, 245, 244, 0.34)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._buttonBar i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-left-color',
		sel: '.comp.compPageEditor._dark ._selectionBar',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Muted Font Color',
		de: 'Hints, placeholders, units and inactive tab labels.',
		n: 'pageEditorDarkFontColorMuted',
		dv: 'rgba(245, 245, 244, 0.50)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle::before',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Strong Border Color',
		de: 'The heavier rule on inputs, drop zones and dividers that must read clearly.',
		n: 'pageEditorDarkBorderColorStrong',
		dv: 'rgba(255, 255, 255, 0.17)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle',
		np: true,
	},
	{
		gn: 'Page Editor Dark Accent',
		dn: 'Accent Color',
		de: 'The primary action colour: add buttons, active toggles, selected outlines.',
		n: 'pageEditorDarkAccentColor',
		dv: '#F59E0B',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._pvEditor ._microToggle._on',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._pillTag',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Secondary Font Color',
		de: 'Labels and secondary copy.',
		n: 'pageEditorDarkFontColorSecondary',
		dv: 'rgba(245, 245, 244, 0.72)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconSelectionEditor',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Font Color',
		de: 'Primary text: input values, headers, selected tabs.',
		n: 'pageEditorDarkFontColor',
		dv: '#F5F5F4',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconSelectionButton',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Active Icon Color',
		de: 'Fill and stroke of an icon whose button is hovered, active or selected.',
		n: 'pageEditorDarkIconActiveColor',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._eachProperty:hover i.fa',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Border Color',
		de: 'The hairline separating panels, rows and toolbars.',
		n: 'pageEditorDarkBorderColor',
		dv: 'rgba(255, 255, 255, 0.09)',
		cp: 'border-color',
		sel: '.comp.compPageEditor._dark ._eachProperty',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Chip Surface',
		de: 'Chips, segmented buttons and inactive tabs, one step off the recessed surface.',
		n: 'pageEditorDarkSurfaceAlt',
		dv: '#232327',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._eachProperty i.fa',
		np: true,
	},

	// Interaction states added on top of the generated set: the pressed wash,
	// the keyboard focus ring, and the icon recolours that the font-awesome
	// glyphs already had but the IconHelper SVGs did not.
	{
		gn: 'Page Editor Surface',
		dn: 'Pressed Wash',
		de: 'The wash under a control while the pointer is held down, one step past the hover wash.',
		n: 'pageEditorSurfacePressed',
		dv: 'rgba(0, 0, 0, 0.09)',
		cp: 'background-color',
		sel: '.comp.compPageEditor ._iconMenu:active, .comp.compPageEditor ._iconMenuBody ._iconMenuOption:active, .comp.compPageEditor ._popupMenuBackground ._popupMenuItem:active, .comp.compPageEditor ._tabContainer ._tab:active, .comp.compPageEditor ._buttonBar i.fa:active, .comp.compPageEditor ._buttonBar svg:active',
		np: true,
	},
	{
		gn: 'Page Editor Dark Surface',
		dn: 'Pressed Wash',
		de: 'The wash under a control while the pointer is held down, one step past the hover wash.',
		n: 'pageEditorDarkSurfacePressed',
		dv: 'rgba(255, 255, 255, 0.12)',
		cp: 'background-color',
		sel: '.comp.compPageEditor._dark ._iconMenu:active, .comp.compPageEditor._dark ._iconMenuBody ._iconMenuOption:active, .comp.compPageEditor._dark ._popupMenuBackground ._popupMenuItem:active, .comp.compPageEditor._dark ._tabContainer ._tab:active, .comp.compPageEditor._dark ._buttonBar i.fa:active, .comp.compPageEditor._dark ._buttonBar svg:active',
		np: true,
	},
	{
		gn: 'Page Editor Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorInfoColor',
		dv: '#1893E9',
		cp: 'color',
		sel: '.comp.compPageEditor ._iconMenu:hover svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Dark Info',
		dn: 'Info Color',
		de: 'The secondary link colour: clickable paths, informational labels.',
		n: 'pageEditorDarkInfoColor',
		dv: '#60A5FA',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._iconMenu:hover svg._iconHelperSVG',
		np: true,
	},
	{
		gn: 'Page Editor Icons',
		dn: 'Active Icon Color',
		de: 'An icon under the cursor or in its selected state.',
		n: 'pageEditorIconActiveColor',
		dv: '#8E90A4',
		cp: 'color',
		sel: '.comp.compPageEditor ._urlInput ._urlInputIcon:hover svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Icons',
		dn: 'Active Icon Color',
		de: 'An icon under the cursor or in its selected state.',
		n: 'pageEditorDarkIconActiveColor',
		dv: 'rgba(245, 245, 244, 0.78)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._urlInput ._urlInputIcon:hover svg',
		np: true,
	},
	{
		gn: 'Page Editor Text',
		dn: 'Strong Font Color',
		de: 'Headings and the pressed state of a control.',
		n: 'pageEditorFontColorStrong',
		dv: '#333333',
		cp: 'color',
		sel: '.comp.compPageEditor ._urlInput ._urlInputIcon:active svg',
		np: true,
	},
	{
		gn: 'Page Editor Dark Text',
		dn: 'Strong Font Color',
		de: 'Headings and the pressed state of a control.',
		n: 'pageEditorDarkFontColorStrong',
		dv: 'rgba(245, 245, 244, 0.92)',
		cp: 'color',
		sel: '.comp.compPageEditor._dark ._urlInput ._urlInputIcon:active svg',
		np: true,
	},
	{
		gn: 'Page Editor Borders',
		dn: 'Focus Border Color',
		de: 'The ring drawn round a control reached by keyboard.',
		n: 'pageEditorFocusBorderColor',
		dv: '#000000',
		cp: 'outline-color',
		sel: '.comp.compPageEditor ._iconMenu:focus-visible, .comp.compPageEditor ._iconMenuOption:focus-visible, .comp.compPageEditor ._popupMenuBackground ._popupMenuItem:focus-visible, .comp.compPageEditor ._tabContainer ._tab:focus-visible, .comp.compPageEditor ._urlInput ._urlInputIcon:focus-visible, .comp.compPageEditor ._buttonBar i.fa:focus-visible, .comp.compPageEditor ._buttonBar svg:focus-visible',
		np: true,
	},
	{
		gn: 'Page Editor Dark Borders',
		dn: 'Focus Border Color',
		de: 'The ring drawn round a control reached by keyboard.',
		n: 'pageEditorDarkFocusBorderColor',
		dv: '#F59E0B',
		cp: 'outline-color',
		sel: '.comp.compPageEditor._dark ._iconMenu:focus-visible, .comp.compPageEditor._dark ._iconMenuOption:focus-visible, .comp.compPageEditor._dark ._popupMenuBackground ._popupMenuItem:focus-visible, .comp.compPageEditor._dark ._tabContainer ._tab:focus-visible, .comp.compPageEditor._dark ._urlInput ._urlInputIcon:focus-visible, .comp.compPageEditor._dark ._buttonBar i.fa:focus-visible, .comp.compPageEditor._dark ._buttonBar svg:focus-visible',
		np: true,
	},
];

export const styleDefaults = new Map<string, string>(
	styleProperties.filter(e => !!e.dv).map(({ n, dv }) => [n, dv!]),
);
